import {
  Suspense,
  type FunctionComponent,
  type PropsWithChildren,
} from 'react';
import { Box, xcss } from '@atlaskit/primitives';
import { media } from '@atlaskit/primitives/compiled';
import MCPFooter from './MCPFooter';
import MCP from './MCP';
import ConsentForm from '../../Flow/ConsentForm';
import { McpContextProvider } from './McpContextProvider';
import {
  useSelectedWorkspace,
  useInfo,
  useToken,
} from '../../Flow/ConsentContextProvider';
import { classifyScopes } from './domain/trelloScopes';
import { useTrelloWorkspaces } from './domain/getTrelloWorkspaces';

const mcpConsentMainStyle = xcss({
  backgroundColor: 'elevation.surface',
  boxSizing: 'border-box',
  padding: 'space.0',
  flex: '1 1 0%',
  minWidth: '0',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
});

const mcpMainViewportStyle = xcss({
  display: 'flex',
  flexDirection: 'column',
  flex: '0 1 auto',
  minHeight: '0',
  width: '100%',
});

const mcpFormFlexStyle = xcss({
  display: 'flex',
  flexDirection: 'column',
  flex: '0 1 auto',
  minHeight: '0',
  width: '100%',
});

const contentGrowMarginStyle = xcss({
  flex: '0 1 auto',
  minWidth: '0',
  display: 'flex',
  flexDirection: 'column',
  // @ts-expect-error — 120px is an intentional custom value not in design tokens
  marginBottom: '120px',
});

const contentGrowNoSelectionStyle = xcss({
  // @ts-expect-error — 500px is an intentional custom value not in design tokens
  marginBottom: '500px',
  [media.above.md]: {
    marginBottom: '0',
  },
});

const ContentGrowWrapper: FunctionComponent<
  PropsWithChildren<{ needsExtraSpace: boolean }>
> = ({ needsExtraSpace, children }) => (
  <Box
    xcss={[
      contentGrowMarginStyle,
      needsExtraSpace && contentGrowNoSelectionStyle,
    ]}
  >
    {children}
  </Box>
);

/** Inner layout that reads MCP context to conditionally adjust spacing. */
const McpConsentInner: FunctionComponent = () => {
  const info = useInfo();
  const { selectedWorkspace } = useSelectedWorkspace();

  const hasWorkspaces =
    Array.isArray(info.availableWorkspaces) &&
    info.availableWorkspaces.length > 0;
  const needsExtraSpace = hasWorkspaces && !selectedWorkspace;

  return (
    <Box as="main" xcss={[mcpConsentMainStyle, mcpMainViewportStyle]}>
      <Box xcss={mcpFormFlexStyle}>
        <ConsentForm>
          <ContentGrowWrapper needsExtraSpace={needsExtraSpace}>
            <MCP />
          </ContentGrowWrapper>
          <MCPFooter />
        </ConsentForm>
      </Box>
    </Box>
  );
};

/**
 * Wrapper that fetches Trello workspaces via AGG when Trello scopes are
 * present, then passes them to McpContextProvider.  Wrapped in Suspense
 * so the Relay query can suspend without blocking the rest of the page.
 */
const TrelloWorkspaceFetcher: FunctionComponent<PropsWithChildren> = ({
  children,
}) => {
  const workspaces = useTrelloWorkspaces();
  return (
    <McpContextProvider trelloWorkspaces={workspaces}>
      {children}
    </McpContextProvider>
  );
};

const McpConsentLayout: FunctionComponent = () => {
  const token = useToken();
  const { hasTrello } = classifyScopes(token.scopes ?? []);

  if (hasTrello) {
    return (
      <Suspense
        fallback={
          <McpContextProvider trelloWorkspacesLoading>
            <McpConsentInner />
          </McpContextProvider>
        }
      >
        <TrelloWorkspaceFetcher>
          <McpConsentInner />
        </TrelloWorkspaceFetcher>
      </Suspense>
    );
  }

  return (
    <McpContextProvider>
      <McpConsentInner />
    </McpContextProvider>
  );
};

export default McpConsentLayout;
